import translateLDSDestinations from "../services/translateLDSDestinations";
import { lookupDestinationByIata } from "./destinationTranslations";

const AIRPORT_CITY_HE_FALLBACK = {
  TLV: "תל אביב",
  DXB: "דובאי",
  JMK: "מיקונוס",
  FCO: "רומא",
  ATH: "אתונה",
};

const IATA_RE = /^[A-Za-z]{3}$/;
const HEBREW_RE = /[\u0590-\u05FF]/;

function normalizeAirportCode(code) {
  const normalized = String(code ?? "")
    .trim()
    .toUpperCase();
  return normalized || null;
}

function looksLikeIata(value) {
  return IATA_RE.test(String(value ?? "").trim());
}

function extractIataFromCityLike(cityLike) {
  if (cityLike == null || cityLike === "—") return "";
  if (typeof cityLike === "string") {
    return looksLikeIata(cityLike) ? cityLike.trim().toUpperCase() : "";
  }
  if (typeof cityLike !== "object") return "";
  const code =
    cityLike.CITYCODE ||
    cityLike.cityCode ||
    cityLike.AIRPORTCODE ||
    cityLike.airportCode ||
    cityLike.code ||
    cityLike.iata;
  return looksLikeIata(code) ? String(code).trim().toUpperCase() : "";
}

function extractDealHebrewName(cityLike) {
  if (!cityLike || typeof cityLike !== "object") return "";
  const heb = String(
    cityLike.CITYNAME_HEB || cityLike.cityNameHeb || cityLike.originCityHe || cityLike.destCityHe || ""
  ).trim();
  if (heb && HEBREW_RE.test(heb)) return heb;
  return "";
}

function extractDealEnglishName(cityLike) {
  if (!cityLike || typeof cityLike !== "object") return "";
  return String(cityLike.CITYNAME || cityLike.cityName || cityLike.name || "").trim();
}

export function airportCodeToCityNameHe(airportCode) {
  const code = normalizeAirportCode(airportCode);
  if (!code) return null;
  const translated = translateLDSDestinations(code);
  const cityNameHe = String(translated?.CITYNAME_HEB || "").trim();
  if (cityNameHe) return cityNameHe;
  return AIRPORT_CITY_HE_FALLBACK[code] || code;
}

/**
 * Hebrew (or English/code fallback) city label from a deal/leg city field or IATA.
 * Prefers deal CITYNAME_HEB when it is real Hebrew; otherwise LDS catalog / curated map.
 */
export function cityNameHeFromCityLike(cityLike) {
  const dealHe = extractDealHebrewName(cityLike);
  if (dealHe) return dealHe;

  const code = extractIataFromCityLike(cityLike);
  if (code) {
    const info = lookupDestinationByIata(code);
    if (info?.labelHe) return info.labelHe;
    return airportCodeToCityNameHe(code) || code;
  }

  if (typeof cityLike === "string") {
    const raw = cityLike.trim();
    if (!raw || raw === "—") return "";
    const translated = translateLDSDestinations(raw);
    const heb = String(translated?.CITYNAME_HEB || "").trim();
    if (heb && HEBREW_RE.test(heb)) return heb;
    return raw;
  }

  return extractDealEnglishName(cityLike);
}

/** Secondary package-card line, e.g. "תל אביב - מיקונוס". Empty when both ends are only IATA. */
export function formatRouteCitiesHe(originCityLike, destCityLike) {
  const from = cityNameHeFromCityLike(originCityLike);
  const to = cityNameHeFromCityLike(destCityLike);
  if (!from && !to) return "";
  if (!from) return to;
  if (!to) return from;
  const fromCode = extractIataFromCityLike(originCityLike);
  const toCode = extractIataFromCityLike(destCityLike);
  if (fromCode && toCode && from === fromCode && to === toCode) return "";
  return `${from} - ${to}`;
}

export function formatAirportDisplayHe(airportCode) {
  const code = normalizeAirportCode(airportCode);
  if (!code) return "—";
  const city = airportCodeToCityNameHe(code);
  if (!city || city === code) return code;
  return `${city} (${code})`;
}

export function formatRouteWithCityNamesHe(originCode, destinationCode) {
  return `${formatAirportDisplayHe(originCode)} → ${formatAirportDisplayHe(destinationCode)}`;
}

